import React, { useState } from 'react';
import Dialog from '@rio-cloud/rio-uikit/lib/es/Dialog';
import { FormattedMessage } from 'react-intl';
import DownloadLogRequestForm
    from '~/features/devices/components/dialogs/DownloadLogRequestBulk/DownloadLogRequestForm';
import { FormProvider, useForm } from 'react-hook-form';
import moment from 'moment/moment';
import { useLimitedDeviceSerials } from '~/features/devices/hooks/useLimitedDeviceSerials';
import { useDownloadLogRequestInBulkMutation } from '~/api/logs/LoggingService.api';
import { BulkRequest } from '~/api/models/BulkRequest';
import download from 'downloadjs';
import Notification from '@rio-cloud/rio-uikit/Notification';
import { CM4 } from '~/features/devices/constants/deviceTypes';

export const MAX_DEVICES = 15;

interface DownloadLogRequestsInBulkDialogProps {
    onHide: () => void;
}

const DownloadLogRequestsInBulkDialog: React.FC<DownloadLogRequestsInBulkDialogProps> = ({
    onHide,
}) => {

    const serialNumbers = useLimitedDeviceSerials(MAX_DEVICES);
    const [downloadLogsInBulk] = useDownloadLogRequestInBulkMutation();
    const [isLoading, setIsLoading] = useState(false);

    const methods = useForm({
        defaultValues: {
            dateInput: {
                startValue: moment().startOf('day'),
                endValue: moment(),
            },
            serialNumberList: serialNumbers,
            requestedSystem: [],
        },
        mode: 'onChange',
    });

    const { handleSubmit, } = methods;

    const onSubmit = async(data) => {
        const requestParams: BulkRequest = {
            devices: data.serialNumberList,
            deviceType: CM4,
            period: {
                minValue: data.dateInput.startValue,
                maxValue: data.dateInput.endValue
            }

        };
        setIsLoading(true);
        await downloadLogsInBulk(requestParams)
            .unwrap()
            .then(async(response) => {
                let totalDevicesWithLogs = 0;
                if (response && Array.isArray(response)) {
                    for (const res of response) {
                        if (res.logDownLoadUrl) {
                            totalDevicesWithLogs++;
                            const response = await fetch(res.logDownLoadUrl);
                            const blob = await response.blob();
                            const filename = `${res.device || 'log'}.zip`;
                            download(blob, filename, 'application/zip');
                        }
                    }
                }
                if (totalDevicesWithLogs === 0) {
                    Notification.error(<FormattedMessage id='intl-msg:logs:noLogsAvailable'/>);
                } else {
                    Notification.success(<FormattedMessage
                        id='intl-msg:logs:downloadBulk:success' values=
                        {{ count: totalDevicesWithLogs }}/>);
                }

                onHide();

            })
            .catch((error) => {
                Notification.error(<FormattedMessage id='intl-msg:logs:logDownloadtWithoutSuccess' values={
                    { error: error?.data?.message }
                }/>);
            }).finally(() => setIsLoading(false));
    };

    const renderBody = () => {
        return (
            <FormProvider {...methods}>
                <DownloadLogRequestForm/>
                <div className='margin-top-20'>
                    <div className='alert alert-info'>
                        <i className='rioglyph rioglyph-info-sign margin-right-5'/>
                        <FormattedMessage
                            id='intl-msg:logs:downloadBulk:disclaimer'/>
                    </div>
                </div>
            </FormProvider>
        );
    };

    const renderFooter = () => {
        return (
            <div className='display-flex justify-content-end'>
                <button
                    type='button'
                    className='btn btn-default margin-right-10'
                    onClick={onHide}
                >
                    <FormattedMessage id='intl-msg:cancel'/>
                </button>
                <button
                    type='button'
                    disabled={isLoading}
                    className={`btn btn-primary ${isLoading && 'btn-loading'}`}
                    onClick={handleSubmit(onSubmit)}
                >
                    <FormattedMessage id='intl-msg:download'/>
                </button>
            </div>
        );
    };

    return (
        <Dialog
            show={true}
            showCloseButton={true}
            onHide={onHide}
            bodyClassName={'visible'}
            title={<FormattedMessage id='intl-msg:downloadLogRequestInBulk'/>}
            body={renderBody()}
            footer={renderFooter()}
        />
    );
};

export default DownloadLogRequestsInBulkDialog;
